// src/hooks/useRole.ts
//
// ⚠️ DEPRECATED: Prefer useCapabilities() for permission checks.
// This hook is kept for backward compatibility and role display purposes.

import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { useCapabilities } from './useCapabilities';
import { StatusLaporan } from '../types';

export interface RoleInfo {
  // Basic role info
  role: string;
  roleName: string;
  displayName: string;
  level: number;

  // ===== Level-based checks (use these for hierarchy) =====
  isTopLevel: boolean;        // Level 0
  isUniversityLevel: boolean; // Level 0-1
  isFacultyLevel: boolean;    // Level 2
  isUnitLevel: boolean;       // Level 3+

  // ===== Legacy role checks (avoid if possible) =====
  isAdmin: boolean;
  isRektor: boolean;
  isWarek: boolean;
  isDekan: boolean;
  isKalem: boolean;
  isKaprodi: boolean;
  isKabag: boolean;
  isKaTU: boolean;
  isLPM: boolean;
  isObserver: boolean;

  // ===== Permission checks (delegated to useCapabilities) =====
  canAccessReportBuilder: boolean;
  canSeeAllUnits: boolean;
  canValidateL1: boolean;
  canValidateL2: boolean;
  canManageRapim: boolean;
  canCreateLaporan: boolean;
  canEditLaporan: (status: StatusLaporan) => boolean;
  canArchive: boolean;
}

const EDITABLE_STATUSES: StatusLaporan[] = ['draf', 'ditolak_l1', 'ditolak_l2'];

export function useRole(): RoleInfo {
  const user = useAuthStore((s) => s.user);
  const caps = useCapabilities();

  return useMemo(() => {
    const roleName = user?.role?.name?.toLowerCase() || '';
    const displayName = user?.role?.display_name || user?.role?.name || '';
    const level = user?.role?.level ?? 99;

    // Legacy role checks (for backward compatibility)
    const isAdmin = roleName === 'admin';
    const isRektor = roleName === 'rektor';
    const isWarek = roleName.startsWith('wakil_rektor_') || ['warek', 'warek1', 'warek2', 'warek3', 'warek4'].includes(roleName);
    const isDekan = roleName === 'dekan';
    const isKalem = roleName === 'kalem';
    const isKaprodi = roleName === 'kaprodi';
    const isKabag = roleName === 'kabag';
    const isKaTU = roleName === 'ka_tu';
    const isLPM = roleName === 'lpm';
    const isObserver = ['lpm', 'senat'].includes(roleName);

    return {
      // Basic info
      role: roleName,
      roleName,
      displayName,
      level,

      // Level-based checks
      isTopLevel: caps.isTopLevel,
      isUniversityLevel: caps.isUniversityLevel,
      isFacultyLevel: caps.isFacultyLevel,
      isUnitLevel: caps.isUnitLevel,

      // Legacy role checks
      isAdmin,
      isRektor,
      isWarek,
      isDekan,
      isKalem,
      isKaprodi,
      isKabag,
      isKaTU,
      isLPM,
      isObserver,

      // Permission checks - delegated to capabilities
      canAccessReportBuilder: caps.canAccessReportBuilder,
      canSeeAllUnits: caps.isUniversityLevel || caps.canViewAllReports,
      canValidateL1: caps.canValidateL1,
      canValidateL2: caps.canValidateL2,
      canManageRapim: caps.canManageRapim,
      canCreateLaporan: caps.canCreateLaporan,

      // Can edit only if status is editable AND has capability
      canEditLaporan: (status: StatusLaporan) => {
        return caps.canCreateLaporan && EDITABLE_STATUSES.includes(status);
      },

      canArchive: caps.canArchive,
    };
  }, [user, caps]);
}
